// BUILD-046 increment 1a: what each COLUMN of the Assigned by Me table says for one request - Task | Assigned to | Status / progress | Priority | Due | Assigned / age | Actions.
//
// A row answers: what did I request, who is responsible NOW, what is its status, when is it due, and when was it assigned and how old is it. Everything here is read from the
// row issuedRequestsModel.ts already builds from `GET /work/issued` (the server's list, under its existing read rules). Nothing is invented and nothing is added to what the list
// already shows: a figure the server did not supply is "Not available".
//
//   Mode badge   direct -> "Single", queue (Anyone) -> "Anyone", set (Everyone) -> "Everyone" (a small badge by the title, not a column).
//   Assigned to  who is responsible NOW:
//                  Single                   the assignee (the task's owner);
//                  Anyone, taken            the claimant (the task's owner);
//                  Anyone, nobody took it   the group(s) the request was sent to, exactly as the list already shows them ("Anyone in <Group>") - NEVER a person;
//                  Everyone                 the recipients the Caller may read (the live ones; the ended ones are in the expansion, with their outcome).
//                A name is the stored display name (the cell draws it with PersonName); a row with nothing to name says "Not available".
//   Status / progress  a status WORD or a COUNT - never a person's name:
//                  Single / Anyone taken    the task's actual status (Active, Waiting, Blocked, Completed) or Cancelled;
//                  Anyone, nobody took it   "Unclaimed";
//                  Everyone                 the server's own figure, "0 of 2 done", with the existing outcome note ("All done", "Request cancelled", "1 removed or cancelled (not counted)").
//   Priority     a Single or taken-Anyone task's own priority. An Everyone request carries none in `GET /work/issued` (the set view has no priority), and a restricted task hides it:
//                  both are "Not available".
//   Due          a Single or Anyone task's Task due date (the page's Phoenix due words and the ONE overdue rule are applied by the cell). An Everyone request carries no due date in
//                  `GET /work/issued` (neither the set nor its members), and a restricted task hides it: both are "Not available".
//   Assigned / age   the request's ORIGINAL assignment time (`createdAt`); assignmentAgeModel.ts writes it, because it depends on the Phoenix day.
//
// SORT KEYS. `issuedSortKeys` gives, per row, the value each Assigned by Me sort orders by (issuedOrdering.ts compares them); a row without a value for a key is "missing" and is
// last in both directions.
//
// Pure module. Runtime imports: the name helpers of displayNames.ts, the priority and status ranks of taskOrdering.ts and the instant helper of assignmentAgeModel.ts (all pure).
// Erasable TypeScript only.

import type { IssuedRow } from './issuedRequestsModel';
import { NOT_AVAILABLE, assignedInstant } from './assignmentAgeModel';
import { isIsoDate } from './dateLabels';
import { fullName, shortName } from './displayNames';
import { PRIORITY_RANK, STATUS_RANK } from './taskOrdering';
import type { OverdueTask } from './overdueRule';

export { NOT_AVAILABLE };

// ───────────────────────── the mode badge ─────────────────────────

export type RequestMode = 'single' | 'anyone' | 'everyone';

export interface ModeBadge {
  mode: RequestMode;
  /** The badge text. */
  label: 'Single' | 'Anyone' | 'Everyone';
  /** The tooltip and accessible detail: what the mode means (the wording the list always used). */
  title: string;
}

const MODE_BADGES: Readonly<Record<IssuedRow['kind'], ModeBadge>> = Object.freeze({
  direct: { mode: 'single', label: 'Single', title: 'Assigned to one person' },
  queue: { mode: 'anyone', label: 'Anyone', title: 'Anyone can complete' },
  set: { mode: 'everyone', label: 'Everyone', title: 'Everyone must complete' },
});

export function modeBadgeOf(row: Pick<IssuedRow, 'kind'>): ModeBadge {
  return MODE_BADGES[row.kind];
}

// ───────────────────────── Assigned to ─────────────────────────

export interface ResponsiblePerson {
  employeeId: string;
  /** The stored display name (PersonName shortens it for display and keeps the full name for the tooltip). */
  name: string;
}

export type ResponsibleCell =
  /** One or more named people: the assignee, the claimant, or the live recipients the Caller may read. */
  | { kind: 'people'; people: ResponsiblePerson[]; title: string }
  /**
   * Nobody has taken an Anyone request: the group(s) and/or the people it was sent to. `prefix` is the words before the names ("Anyone in " for a group, "Anyone of " when it went to chosen people only) and
   * `text` is the cell's words ("Anyone in Accounting", "Anyone of Eddie T., Julie R.").
   */
  | { kind: 'group'; labels: string[]; prefix: string; text: string; title: string }
  /** Nothing to name: `text` is "Loading…", "Not available" or "None active". */
  | { kind: 'none'; text: string; title: string };

function cleanName(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function peopleCell(people: ResponsiblePerson[], title: string): ResponsibleCell {
  return { kind: 'people', people, title };
}

export function responsibleOf(row: IssuedRow): ResponsibleCell {
  if (row.status === 'loading') return { kind: 'none', text: 'Loading…', title: 'Loading' };
  if (row.status === 'unavailable') return { kind: 'none', text: NOT_AVAILABLE, title: row.error ?? 'This request could not be read' };
  if (row.kind === 'set') {
    const live = row.members.filter((member) => !member.ended);
    if (live.length > 0) return peopleCell(live.map((member) => ({ employeeId: member.employeeId, name: member.displayName })), 'Recipients you can see');
    if (row.members.length > 0) return { kind: 'none', text: 'None active', title: 'Every recipient you can see was removed or cancelled' };
    return { kind: 'none', text: NOT_AVAILABLE, title: 'No recipients you can see' };
  }
  const task = row.task;
  if (task === null) return { kind: 'none', text: NOT_AVAILABLE, title: 'The task could not be read' };
  const owner = cleanName(task.ownerName);
  const taken = !task.unclaimed && owner !== '';
  if (taken) return peopleCell([{ employeeId: task.ownerEmployeeId, name: owner }], row.kind === 'direct' ? 'Assigned to' : 'Taken by');
  // Nobody took it (or a direct task whose owner name was not supplied): a group the request was sent to is shown as the list always showed it; a person is never shown as responsible.
  // Owner direction 2026-10-23: an Anyone request sent to chosen PEOPLE (not a group) names them, from the task's own recipient list; it used to say "Not available". Groups come first, then people.
  if (row.kind === 'queue') {
    const groups = row.groupLabels;
    const named = task.recipients.map((person) => ({ short: shortName(person.displayName), full: fullName(person.displayName) })).filter((person) => person.short !== '');
    const labels = [...groups, ...named.map((person) => person.short)];
    if (labels.length > 0) {
      const prefix = groups.length > 0 ? 'Anyone in ' : 'Anyone of ';
      const sentTo = named.length > 0 ? `. Sent to: ${[...groups, ...named.map((person) => person.full)].join(', ')}` : '';
      return { kind: 'group', labels, prefix, text: `${prefix}${labels.join(', ')}`, title: `Nobody has taken this yet${sentTo}` };
    }
  }
  return { kind: 'none', text: NOT_AVAILABLE, title: row.kind === 'direct' ? 'The server did not name the assignee' : 'Who can take this is not listed' };
}

// ───────────────────────── Status / progress ─────────────────────────

export type ProgressKind = 'loading' | 'unavailable' | 'status' | 'unclaimed' | 'count' | 'none';

export interface ProgressCell {
  kind: ProgressKind;
  /** A status word, "Unclaimed", a count ("0 of 2 done") or "Loading…" / "Unavailable" / "Not available". NEVER a person's name. */
  text: string;
  /** The quiet extra words (an Everyone request's outcome note), or null. */
  detail: string | null;
}

const STATUS_WORD: Readonly<Record<string, string>> = Object.freeze({ active: 'Active', in_progress: 'In progress', waiting: 'Waiting', blocked: 'Blocked', completed: 'Completed' });

function endedTask(task: { lifecycle: string; outcome: string }): boolean {
  return task.lifecycle === 'cancelled' || task.outcome === 'removed' || task.outcome === 'cancelled';
}

/** True for an Anyone request nobody has taken and that is still live (not completed, not cancelled). */
function isUnclaimed(row: IssuedRow): boolean {
  const task = row.task;
  return row.kind === 'queue' && task !== null && task.unclaimed && task.status !== 'completed' && !endedTask(task);
}

export function progressOf(row: IssuedRow): ProgressCell {
  if (row.status === 'loading') return { kind: 'loading', text: 'Loading…', detail: null };
  if (row.status === 'unavailable') return { kind: 'unavailable', text: 'Unavailable', detail: row.error };
  if (row.kind === 'set') {
    if (row.label === null) return { kind: 'none', text: NOT_AVAILABLE, detail: null };
    // The server's own figure ("2 of 3"), said as a count of what is done; it is recomputed from the numbers only by the model, never here.
    const text = /^\d+ of \d+$/.test(row.label) ? `${row.label} done` : row.label;
    return { kind: 'count', text, detail: row.note };
  }
  const task = row.task;
  if (task === null) return { kind: 'none', text: NOT_AVAILABLE, detail: null };
  if (endedTask(task)) return { kind: 'status', text: 'Cancelled', detail: null };
  if (isUnclaimed(row)) return { kind: 'unclaimed', text: 'Unclaimed', detail: null };
  const word = STATUS_WORD[task.status];
  return word === undefined ? { kind: 'none', text: NOT_AVAILABLE, detail: null } : { kind: 'status', text: word, detail: null };
}

// ───────────────────────── Priority and Due ─────────────────────────

export type PriorityCell =
  | { kind: 'priority'; value: 'low' | 'medium' | 'high' | 'urgent' }
  /** The task carries no priority (an older request): the same dash as every other table. */
  | { kind: 'none' }
  /** An Everyone request whose recipients' own tasks carry different priorities: the cell says "Varies" and `title` lists each recipient's. Nothing is picked, averaged or invented. */
  | { kind: 'varies'; title: string }
  | { kind: 'unavailable'; title: string };

const RESTRICTED_TITLE = 'Not shown: this task is restricted';
const NO_LIVE_RECIPIENTS_TITLE = 'Every recipient you can see was removed or cancelled';
const SOME_RESTRICTED_TITLE = 'Not shown: at least one recipient\'s task is restricted';
const PRIORITY_WORD: Readonly<Record<string, string>> = Object.freeze({ low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' });

type SetMember = IssuedRow['members'][number];

/** The live recipients of an Everyone request whose own tasks can be read, or the reason there is nothing to aggregate. */
function livePick(row: IssuedRow): { live: SetMember[]; title: string | null } {
  if (row.status !== 'ready') return { live: [], title: 'The request could not be read' };
  const live = row.members.filter((member) => !member.ended);
  if (live.length === 0) return { live, title: NO_LIVE_RECIPIENTS_TITLE };
  if (live.some((member) => member.restricted)) return { live, title: SOME_RESTRICTED_TITLE };
  return { live, title: null };
}

function priorityKeyOf(value: string): '' | 'low' | 'medium' | 'high' | 'urgent' {
  const key = value.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(PRIORITY_RANK, key) ? (key as 'low' | 'medium' | 'high' | 'urgent') : '';
}

/** 2026-11-05 as 11/05/2026 (the date style of the History and the forms). */
function usDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match === null ? iso : `${match[2]}/${match[3]}/${match[1]}`;
}

export function priorityOf(row: IssuedRow): PriorityCell {
  if (row.kind === 'set') {
    // Owner direction 2026-10-23: an Everyone request shows its recipients' OWN priorities - one value when they all agree, "Varies" (with each recipient's in the hover text) when they differ.
    const pick = livePick(row);
    if (pick.title !== null) return { kind: 'unavailable', title: pick.title };
    const keys = pick.live.map((member) => priorityKeyOf(member.priority));
    const distinct = Array.from(new Set(keys));
    if (distinct.length === 1) {
      const only = distinct[0];
      return only === '' ? { kind: 'none' } : { kind: 'priority', value: only };
    }
    return { kind: 'varies', title: `The recipients differ. ${pick.live.map((member, index) => `${shortName(member.displayName)}: ${keys[index] === '' ? 'No priority' : PRIORITY_WORD[keys[index]]}`).join('; ')}` };
  }
  const task = row.task;
  if (row.status !== 'ready' || task === null) return { kind: 'unavailable', title: 'The task could not be read' };
  if (task.redacted) return { kind: 'unavailable', title: RESTRICTED_TITLE };
  const key = task.priority.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(PRIORITY_RANK, key) ? { kind: 'priority', value: key as 'low' | 'medium' | 'high' | 'urgent' } : { kind: 'none' };
}

export type DueData =
  /** The task's Task due date ('' when it has none) and the fields the ONE overdue rule reads; the cell applies the rule with the page's Phoenix day. */
  | { kind: 'date'; dueDate: string; overdueInput: OverdueTask }
  /** An Everyone request whose recipients' own tasks carry different due dates: "Varies", with each recipient's date in `title`. */
  | { kind: 'varies'; title: string }
  | { kind: 'unavailable'; title: string };

export function dueOf(row: IssuedRow): DueData {
  if (row.kind === 'set') {
    const pick = livePick(row);
    if (pick.title !== null) return { kind: 'unavailable', title: pick.title };
    const dates = pick.live.map((member) => (isIsoDate(member.dueDate) ? member.dueDate : ''));
    const distinct = Array.from(new Set(dates));
    if (distinct.length === 1) {
      // One shared date (or none): it is overdue by THE one rule when any recipient's task is still open.
      const open = pick.live.some((member) => member.status !== 'completed');
      return { kind: 'date', dueDate: distinct[0], overdueInput: { status: open ? 'active' : 'completed', dueDate: distinct[0], overdue: false, lifecycle: '', outcome: '', redacted: false } };
    }
    return { kind: 'varies', title: `The recipients differ. ${pick.live.map((member, index) => `${shortName(member.displayName)}: ${dates[index] === '' ? 'No due date' : usDate(dates[index])}`).join('; ')}` };
  }
  const task = row.task;
  if (row.status !== 'ready' || task === null) return { kind: 'unavailable', title: 'The task could not be read' };
  if (task.redacted) return { kind: 'unavailable', title: RESTRICTED_TITLE };
  return { kind: 'date', dueDate: task.dueDate, overdueInput: { status: task.status, dueDate: task.dueDate, overdue: task.overdue, lifecycle: task.lifecycle, outcome: task.outcome, redacted: task.redacted } };
}

// ───────────────────────── the Everyone expansion ─────────────────────────

export interface RecipientDetail {
  employeeId: string;
  name: string;
  /** "Active", "Waiting", "Blocked", "Completed", "Removed (ended, not completed)", "Cancelled (ended, not completed)" or "Unknown". */
  statusText: string;
  ended: boolean;
  /** That child task's Task due date ('' when it has none), or null when the data cannot say (an ended recipient, or a restricted task: see `dueAvailable`). */
  dueDate: string | null;
  /** That child task's priority word ("High"), "No priority", or null when the data cannot say. */
  priorityText: string | null;
}

export interface RecipientsDetail {
  /** The recipients the Caller may read, live and ended, in the order the server gave them. Nobody the list does not already show. */
  recipients: RecipientDetail[];
  /** False when a live recipient's task is restricted (no figure to show), so the expansion says so once instead of inventing one. */
  dueAvailable: boolean;
  /** The sentence over the list ("Recipients you can see (2)"), exactly the one the list always used. */
  heading: string;
}

function memberStatusText(member: IssuedRow['members'][number]): string {
  if (member.ended) return member.outcome === 'removed' ? 'Removed (ended, not completed)' : 'Cancelled (ended, not completed)';
  const status = member.status ?? '';
  return status === '' ? 'Unknown' : `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

/** The expansion of an Everyone row, or null for any other kind and for an Everyone request with nothing the Caller may read. */
export function recipientsDetailOf(row: IssuedRow): RecipientsDetail | null {
  if (row.kind !== 'set' || row.members.length === 0) return null;
  return {
    recipients: row.members.map((member) => {
      const known = !member.ended && !member.restricted;
      return {
        employeeId: member.employeeId,
        name: member.displayName,
        statusText: memberStatusText(member),
        ended: member.ended,
        dueDate: known ? (isIsoDate(member.dueDate) ? member.dueDate : '') : null,
        priorityText: known ? (priorityKeyOf(member.priority) === '' ? 'No priority' : PRIORITY_WORD[priorityKeyOf(member.priority)]) : null,
      };
    }),
    dueAvailable: !row.members.some((member) => !member.ended && member.restricted),
    heading: `Recipients you can see (${row.members.length})`,
  };
}

// ───────────────────────── sort keys ─────────────────────────

/** Where a row stands for the Status / progress sort: a band (the ONE order) and, inside a band, the share done (0 to 1). */
export interface ProgressRank {
  band: number;
  share: number;
}

/**
 * The Status / progress order, ascending: Unclaimed (0), Active (1), In progress (2), Waiting (3), Blocked (4), Completed (5), Cancelled (6). An Everyone request counts as Active until every
 * recipient is done (then Completed) and as Cancelled when the request was cancelled; inside a band the request with the smaller share done comes first (a Single or Anyone task
 * is 0, or 1 when completed). Descending is the exact reverse.
 */
export const PROGRESS_ORDER_TEXT = 'Unclaimed, Active, In progress, Waiting, Blocked, Completed, Cancelled';

function progressRankOf(row: IssuedRow): ProgressRank | null {
  if (row.status !== 'ready') return null;
  if (row.kind === 'set') {
    const figure = row.figure;
    if (figure === null) return null;
    const share = figure.m > 0 ? figure.n / figure.m : 0;
    if (figure.setCancelled) return { band: 6, share };
    if (figure.allDone || (figure.m > 0 && figure.n === figure.m)) return { band: 5, share: 1 };
    return { band: 1, share };
  }
  const task = row.task;
  if (task === null) return null;
  if (endedTask(task)) return { band: 6, share: 0 };
  if (isUnclaimed(row)) return { band: 0, share: 0 };
  const rank = Object.prototype.hasOwnProperty.call(STATUS_RANK, task.status) ? STATUS_RANK[task.status] : null;
  if (rank === null) return null;
  return { band: rank + 1, share: task.status === 'completed' ? 1 : 0 };
}

export interface IssuedSortKeys {
  /** The stored title (as the other tables sort it), or null when empty. */
  title: string | null;
  /** The first name listed in Assigned to (the assignee, the claimant, the first group of an untaken Anyone request, the first recipient of an Everyone request), or null. */
  responsible: string | null;
  priority: number | null;
  /** The request's Task due date (YYYY-MM-DD), or null. */
  due: string | null;
  progress: ProgressRank | null;
  /** The original assignment instant in milliseconds, or null. */
  assigned: number | null;
}

export function issuedSortKeys(row: IssuedRow): IssuedSortKeys {
  const responsible = responsibleOf(row);
  const first = responsible.kind === 'people' ? cleanName(responsible.people[0]?.name) : responsible.kind === 'group' ? cleanName(responsible.labels[0]) : '';
  // "Varies" and "Not available" have no single value to order by, so they are last under their rule (never a guessed one).
  const priority = priorityOf(row);
  const due = dueOf(row);
  return {
    title: row.title.trim() === '' ? null : row.title.trim(),
    responsible: first === '' ? null : first,
    priority: priority.kind === 'priority' ? PRIORITY_RANK[priority.value] : null,
    due: due.kind === 'date' && isIsoDate(due.dueDate) ? due.dueDate : null,
    progress: progressRankOf(row),
    assigned: assignedInstant(row.createdAt),
  };
}

// ───────────────────────── everything a row's cells need ─────────────────────────

export interface IssuedColumns {
  mode: ModeBadge;
  responsible: ResponsibleCell;
  progress: ProgressCell;
  priority: PriorityCell;
  due: DueData;
  /** The request's original assignment instant (assignmentAgeModel.describeAssignedAge turns it into the cell, with the page's Phoenix day). */
  createdAt: string | null;
  recipients: RecipientsDetail | null;
}

export function describeIssuedColumns(row: IssuedRow): IssuedColumns {
  return {
    mode: modeBadgeOf(row),
    responsible: responsibleOf(row),
    progress: progressOf(row),
    priority: priorityOf(row),
    due: dueOf(row),
    createdAt: row.createdAt,
    recipients: recipientsDetailOf(row),
  };
}
